import type { Block, Document } from '@gitbook/slate';
import { getEmojiForCode } from '@gitbook/util-emoji-rendering';
import { List } from '@gitbook/util-immutable';

import * as expandable from '../expandable';
import {
    BLOCK_COLUMN,
    BLOCK_COLUMNS,
    BLOCK_EXPANDABLE,
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_OPENAPI,
    BLOCK_OPENAPI_OPERATION,
    BLOCK_OPENAPI_SCHEMAS,
    BLOCK_OPENAPI_WEBHOOK,
    BLOCK_STEPPER,
    BLOCK_STEPPER_STEP,
    BLOCK_TABS,
    BLOCK_TABS_ITEM,
    BLOCK_UPDATE,
    BLOCK_UPDATES,
    INLINE_EMOJI,
    INLINE_LINK,
} from '../types';
import { getNodeTextForID, getSectionIDs, type SectionIDs } from './getSectionIDs';
import {
    isSectionDelimiter,
    isSectionDelimiterParent,
    type SectionDelimiterBlock,
    type SectionDelimiterType,
} from './isSectionDelimiter';

/**
 * A section represents a chunk of the document that is addressable by a URL.
 * It used to help the user better navigate the document through the page outline,
 * but also for content indexing where we want to index more semantic chunks.
 */
export interface DocumentSection {
    /**
     * The section delimiter block.
     * It can be null if the section is empty or if the section is not a delimiter block.
     */
    node: SectionDelimiterBlock | null;

    /** Type */
    type: SectionDelimiterType | null;

    /**
     * Type of the parent of this section. If not `document`, then this section is a nested
     * section and should be handled as such. Will be null when returning an empty section with
     * a null type.
     */
    parentType: string | null;

    /**
     * Key of the starting block in the document.
     * It can be used as an unique identifier for the section.
     */
    key: string;

    /**
     * ID in the url for this section.
     * It can be an empty string for the first section of the document.
     */
    anchor: string;

    /**
     * Plain text title of the section.
     */
    title: string;

    /**
     * Nodes contained in the section.
     * It represents the body of the section, and depending on the block, it can include or not
     * the delimiter block.
     */
    nodes: List<Block>;
}

/**
 * Cache to optimize multiple calls to getSectionsInDoc when rendering the application.
 */
const sectionInDocsCache = new WeakMap<Document, DocumentSection[]>();
const indexableSectionInDocsCache = new WeakMap<Document, DocumentSection[]>();

/**
 * List all sections in a document.
 */
export function getSectionsInDoc(
    document: Document,
    sectionIDs: SectionIDs = getSectionIDs(document)
): DocumentSection[] {
    const inCache = sectionInDocsCache.get(document);
    if (inCache) {
        return inCache;
    }

    const sections = getSectionsInNodes(document.nodes, sectionIDs, document.object, {
        includeUntitledStepperSections: false,
    });
    sectionInDocsCache.set(document, sections);
    return sections;
}

/**
 * List all sections in a document that should be used for content indexing.
 * Unlike `getSectionsInDoc`, this preserves untitled content chunks inside stepper steps so
 * search indexation does not drop body content that is not addressable by an anchor.
 */
export function getIndexableSectionsInDoc(
    document: Document,
    sectionIDs: SectionIDs = getSectionIDs(document)
): DocumentSection[] {
    const inCache = indexableSectionInDocsCache.get(document);
    if (inCache) {
        return inCache;
    }

    const sections = getSectionsInNodes(document.nodes, sectionIDs, document.object, {
        includeUntitledStepperSections: true,
    });
    indexableSectionInDocsCache.set(document, sections);
    return sections;
}

function getSectionsInNodes(
    nodes: List<Block>,
    sectionIDs: SectionIDs,
    parentType: string,
    options: {
        includeUntitledStepperSections: boolean;
    }
): DocumentSection[] {
    const result: DocumentSection[] = [];

    while (!nodes.isEmpty()) {
        const firstNode = nodes.first() as Block;

        if (firstNode.type === BLOCK_EXPANDABLE) {
            // The body of an expandable lives in its `expandable-body` fragment, not in `blockNodes`,
            // so we read the headings (and the leading content) from there.
            const bodyNodes = expandable.getBodyFragment(firstNode).nodes as List<Block>;
            const nextDelimiterIndex = bodyNodes.findIndex(
                (node) => isSectionDelimiterParent(node) || isSectionDelimiter(node)
            );
            const endSectionIndex = nextDelimiterIndex >= 0 ? nextDelimiterIndex : bodyNodes.size;

            // Create a section for the expandable itself, holding the body content up to its first heading.
            result.push(
                createSectionForNode(firstNode as SectionDelimiterBlock, sectionIDs, {
                    parentType,
                    nodes: bodyNodes.slice(0, endSectionIndex),
                })
            );

            // Recurse into the rest of the body to emit a section for each heading it contains,
            // and, for indexing, preserved untitled content from nested steppers.
            result.push(
                ...getSectionsInNodes(
                    bodyNodes.slice(endSectionIndex),
                    sectionIDs,
                    firstNode.type,
                    options
                ).filter(
                    (block) =>
                        block.type ||
                        (options.includeUntitledStepperSections &&
                            block.parentType === BLOCK_STEPPER)
                )
            );

            nodes = nodes.rest();
        } else if ([BLOCK_COLUMNS, BLOCK_TABS].includes(firstNode.type)) {
            // We look through all the block node and then get those nodes indexed as sections.
            firstNode.blockNodes.forEach((child) => {
                if (
                    (firstNode.type === BLOCK_TABS && child.type !== BLOCK_TABS_ITEM) ||
                    (firstNode.type === BLOCK_COLUMNS && child.type !== BLOCK_COLUMN)
                ) {
                    return;
                }

                const childNodes = child.nodes as List<Block>;
                const nextDelimiterIndex = childNodes.findIndex(isSectionDelimiter);
                const endSectionIndex =
                    nextDelimiterIndex >= 0 ? nextDelimiterIndex : childNodes.size;

                result.push(
                    createSectionForNode(child as SectionDelimiterBlock, sectionIDs, {
                        parentType,
                        // For columns and tabs, we want to make sure we add the content of each column/tab item up to the
                        // next section _within_ that column/tab item.
                        nodes: childNodes.slice(0, endSectionIndex),
                    })
                );

                // Filter out sections with null type (leading content before the first heading)
                // since we only want actual typed sections from nested content.
                result.push(
                    ...getSectionsInNodes(
                        childNodes.slice(endSectionIndex),
                        sectionIDs,
                        firstNode.type,
                        options
                    ).filter((block) => block.type)
                );
            });

            nodes = nodes.rest();
        } else if (firstNode.type === BLOCK_STEPPER) {
            firstNode.blockNodes.forEach((child) => {
                if (child.type !== BLOCK_STEPPER_STEP) {
                    return;
                }

                const stepNodes = child.nodes as List<Block>;
                const stepHeadingNode = stepNodes.first();

                // Create a section for the step itself if it starts with a heading
                if (
                    stepHeadingNode &&
                    [BLOCK_HEADING_1, BLOCK_HEADING_2].includes(stepHeadingNode.type)
                ) {
                    result.push(
                        createSectionForNode(child as SectionDelimiterBlock, sectionIDs, {
                            parentType,
                            nodes: stepNodes.slice(0, 1),
                        })
                    );
                }

                // Recurse into the step body for nested sections and, for indexing, preserved untitled content.
                result.push(
                    ...getSectionsInNodes(stepNodes, sectionIDs, firstNode.type, options).filter(
                        (block) =>
                            block.type ||
                            (options.includeUntitledStepperSections &&
                                block.parentType === BLOCK_STEPPER)
                    )
                );
            });

            nodes = nodes.rest();
        } else if (firstNode.type === BLOCK_UPDATES) {
            result.push(
                ...getSectionsInNodes(
                    firstNode.blockNodes,
                    sectionIDs,
                    firstNode.type,
                    options
                ).filter((block) => block.type)
            );

            nodes = nodes.rest();
        } else if (firstNode.type === BLOCK_UPDATE) {
            // Emit a section for the update block itself (anchored by date)
            result.push(
                createSectionForNode(firstNode as SectionDelimiterBlock, sectionIDs, {
                    parentType,
                    nodes: firstNode.blockNodes,
                })
            );

            // Also recurse into the update's children for heading-level sections
            result.push(
                ...getSectionsInNodes(
                    firstNode.blockNodes,
                    sectionIDs,
                    firstNode.type,
                    options
                ).filter((block) => block.type)
            );

            nodes = nodes.rest();
        } else {
            const restNodes = nodes.rest();
            const nextDelimiterIndex = restNodes.findIndex(
                (node) => isSectionDelimiterParent(node) || isSectionDelimiter(node)
            );
            const endSectionIndex = nextDelimiterIndex >= 0 ? nextDelimiterIndex : restNodes.size;

            const nodesUntilNextSection = restNodes.slice(0, endSectionIndex);

            if (isSectionDelimiter(firstNode)) {
                result.push(
                    createSectionForNode(firstNode, sectionIDs, {
                        parentType,
                        nodes: nodesUntilNextSection,
                    })
                );
            } else {
                result.push({
                    node: null,
                    type: null,
                    parentType,
                    key: firstNode.key,
                    title: '',
                    anchor: '',
                    nodes: nodesUntilNextSection.unshift(firstNode),
                });
            }

            if (nextDelimiterIndex < 0) {
                break;
            }
            nodes = restNodes.slice(nextDelimiterIndex);
        }
    }

    return result;
}

function createSectionForNode(
    node: SectionDelimiterBlock,
    sectionIDs: SectionIDs,
    partial: Partial<DocumentSection> = {}
): DocumentSection {
    return {
        node,
        type: node.type,
        parentType: 'document',
        key: node.key,
        title: getNodeTitle(node),
        anchor: sectionIDs.get(node.key) || '',
        nodes: List.create(),
        ...partial,
    };
}

function getNodeTitle(node: SectionDelimiterBlock): string {
    switch (node.type) {
        case BLOCK_OPENAPI:
        case BLOCK_OPENAPI_OPERATION:
        case BLOCK_OPENAPI_SCHEMAS:
        case BLOCK_OPENAPI_WEBHOOK:
        case BLOCK_TABS_ITEM:
        case BLOCK_EXPANDABLE:
        case BLOCK_UPDATE:
            return getNodeTextForID(node);
        default: {
            return getNodeTitleWithEmoji(node);
        }
    }
}

function getNodeTitleWithEmoji(node: SectionDelimiterBlock): string {
    return node.nodes.reduce((acc, n) => {
        // If the title includes an emoji, parse it to include its string version
        if (n.type === INLINE_EMOJI && n.data.has('code')) {
            const emojiString = getEmojiForCode(n.data.get('code')) ?? '';
            return acc + emojiString;
        }
        // If the title includes a link, return its text content
        if (n.type === INLINE_LINK && n.text.length > 0) {
            return acc + n.text;
        }
        if (n.object === 'text' && n.text.length > 0) {
            return acc + n.text;
        }
        return acc;
    }, '');
}
